import React, { useState, useEffect } from 'react';
import { Send, Plus, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import api from '../../lib/api';

const BroadcastsPage = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    bot_id: '',
    name: '',
    message: '',
    target_tags: []
  });

  useEffect(() => {
    fetchBots();
    fetchBroadcasts();
  }, []);

  const fetchBots = async () => {
    try {
      const response = await api.get('/api/bots');
      if (response.ok) {
        const data = await response.json();
        setBots(data);
        if (data.length > 0) setFormData(prev => ({ ...prev, bot_id: data[0].bot_id }));
      }
    } catch (error) {
      console.error('Failed to fetch bots:', error);
    }
  };

  const fetchBroadcasts = async () => {
    try {
      const response = await api.get('/api/broadcasts');
      if (response.ok) {
        const data = await response.json();
        setBroadcasts(data);
      }
    } catch (error) {
      console.error('Failed to fetch broadcasts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await api.post('/api/broadcasts', formData);
      if (response.ok) {
        const newBroadcast = await response.json();
        setBroadcasts([...broadcasts, newBroadcast]);
        setIsCreateOpen(false);
        setFormData({ bot_id: bots[0]?.bot_id || '', name: '', message: '', target_tags: [] });
      }
    } catch (error) {
      console.error('Failed to create broadcast:', error);
    }
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.bot_id === botId);
    return bot?.name || 'Bot';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Broadcasts</h1>
          <p className="text-gray-500">Envie mensagens em massa</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb]" disabled={bots.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Broadcast
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Criar Broadcast</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome da Campanha</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Promoção de Verão"
                />
              </div>
              <div>
                <Label>Bot</Label>
                <Select
                  value={formData.bot_id}
                  onValueChange={(value) => setFormData({ ...formData, bot_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um bot" />
                  </SelectTrigger>
                  <SelectContent>
                    {bots.map((bot) => (
                      <SelectItem key={bot.bot_id} value={bot.bot_id}>
                        {bot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mensagem</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Digite a mensagem..."
                  rows={4}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} className="bg-[#3b82f6] hover:bg-[#2563eb]">
                  Criar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {bots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Send className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Crie um bot primeiro</h3>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="text-center py-12">Carregando...</div>
      ) : broadcasts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Send className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhum broadcast</h3>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {broadcasts.map((broadcast) => (
            <Card key={broadcast.broadcast_id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{broadcast.name}</h3>
                    <p className="text-sm text-gray-500">{getBotName(broadcast.bot_id)}</p>
                  </div>
                  <Badge className={broadcast.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {broadcast.status === 'sent' ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                    {broadcast.status}
                  </Badge>
                </div>
                <p className="mt-3 text-sm text-gray-600 line-clamp-2">{broadcast.message}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                  <span>Destinatários: {broadcast.recipients_count}</span>
                  <span>{new Date(broadcast.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BroadcastsPage;
